/**
 * Browser support detection and performance target validation
 */

interface BrowserInfo {
  name: string;
  version: string;
  engine: string;
  isSupported: boolean;
  supportedFeatures: string[];
  unsupportedFeatures: string[];
}

interface PerformanceTarget {
  name: string;
  target: number;
  current: number;
  passed: boolean;
  unit: string;
}

interface BrowserSupportResult {
  browser: BrowserInfo;
  performanceTargets: PerformanceTarget[];
  overallSupport: boolean;
  recommendations: string[];
}

class BrowserSupportDetector {
  private readonly SUPPORTED_BROWSERS = {
    chrome: { minVersion: 90, engine: 'Blink' },
    firefox: { minVersion: 88, engine: 'Gecko' },
    safari: { minVersion: 14, engine: 'WebKit' },
    edge: { minVersion: 90, engine: 'Blink' },
  };

  private readonly REQUIRED_FEATURES = [
    'webgl',
    'webgl2',
    'offscreenCanvas',
    'requestIdleCallback',
    'intersectionObserver',
    'resizeObserver',
    'webassembly',
    'indexedDB2',
    'cssGrid',
    'flexbox',
    'customProperties',
    'promise',
    'asyncAwait',
    'modules',
    'serviceWorker',
    'webp',
    'avif'
  ];

  private readonly PERFORMANCE_TARGETS = {
    fcp: { target: 2500, unit: 'ms', name: 'First Contentful Paint' },
    lcp: { target: 2500, unit: 'ms', name: 'Largest Contentful Paint' },
    fid: { target: 100, unit: 'ms', name: 'First Input Delay' },
    cls: { target: 0.1, unit: '', name: 'Cumulative Layout Shift' },
    ttfb: { target: 800, unit: 'ms', name: 'Time to First Byte' },
    simulationTime: { target: 50, unit: 'ms', name: 'Substrate Simulation Time' },
    memoryUsage: { target: 100, unit: 'MB', name: 'Memory Usage' }
  };

  async detectBrowser(): Promise<BrowserInfo> {
    const userAgent = navigator.userAgent;
    const browserInfo = this.parseUserAgent(userAgent);
    const supportedFeatures = await this.getSupportedFeatures();
    const unsupportedFeatures = this.REQUIRED_FEATURES.filter(
      feature => !supportedFeatures.includes(feature)
    );

    return {
      ...browserInfo,
      supportedFeatures,
      unsupportedFeatures,
      isSupported: this.isBrowserSupported(browserInfo, unsupportedFeatures)
    };
  }

  private parseUserAgent(userAgent: string): Omit<BrowserInfo, 'supportedFeatures' | 'unsupportedFeatures' | 'isSupported'> {
    let name = 'unknown';
    let version = '0';
    let engine = 'unknown';

    // Chrome/Chromium/Edge
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
      name = 'chrome';
      version = userAgent.match(/Chrome\/(\d+)/)?.[1] || '0';
      engine = 'Blink';
    }
    // Edge (Chromium-based)
    else if (/Edg/.test(userAgent)) {
      name = 'edge';
      version = userAgent.match(/Edg\/(\d+)/)?.[1] || '0';
      engine = 'Blink';
    }
    // Firefox
    else if (/Firefox/.test(userAgent)) {
      name = 'firefox';
      version = userAgent.match(/Firefox\/(\d+)/)?.[1] || '0';
      engine = 'Gecko';
    }
    // Safari
    else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      name = 'safari';
      const versionMatch = userAgent.match(/Version\/(\d+)/) || userAgent.match(/Safari\/(\d+)/);
      version = versionMatch?.[1] || '0';
      engine = 'WebKit';
    }

    return { name, version, engine };
  }

  private async getSupportedFeatures(): Promise<string[]> {
    const features: string[] = [];

    // WebGL support
    if (this.checkWebGL()) features.push('webgl');
    if (this.checkWebGL2()) features.push('webgl2');

    // Canvas features
    if ('OffscreenCanvas' in window) features.push('offscreenCanvas');

    // APIs
    if ('requestIdleCallback' in window) features.push('requestIdleCallback');
    if ('IntersectionObserver' in window) features.push('intersectionObserver');
    if ('ResizeObserver' in window) features.push('resizeObserver');
    if ('WebAssembly' in window) features.push('webassembly');
    if ('serviceWorker' in navigator) features.push('serviceWorker');

    // IndexedDB 2.0
    if (this.checkIndexedDB2()) features.push('indexedDB2');

    // CSS features
    if (this.checkCSSFeature('grid')) features.push('cssGrid');
    if (this.checkCSSFeature('flexbox')) features.push('flexbox');
    if (CSS.supports('color', 'var(--test)')) features.push('customProperties');

    // JavaScript features
    if (this.checkPromise()) features.push('promise');
    if (this.checkAsyncAwait()) features.push('asyncAwait');
    if (this.checkModules()) features.push('modules');

    // Image formats
    if (this.checkWebPSupport()) features.push('webp');
    if (await this.checkAVIFSupport()) features.push('avif');

    return features;
  }

  private checkWebGL(): boolean {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      return gl !== null;
    } catch {
      return false;
    }
  }

  private checkWebGL2(): boolean {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      return gl !== null;
    } catch {
      return false;
    }
  }

  private checkIndexedDB2(): boolean {
    return 'indexedDB' in window && 
           IDBKeyRange !== undefined &&
           IDBCursor !== undefined;
  }

  private checkCSSFeature(feature: string): boolean {
    switch (feature) {
      case 'grid':
        return CSS.supports('display', 'grid') || CSS.supports('display', 'ms-grid');
      case 'flexbox':
        return CSS.supports('display', 'flex') || CSS.supports('display', '-webkit-flex') || CSS.supports('display', '-ms-flexbox');
      default:
        return false;
    }
  }

  private checkPromise(): boolean {
    try {
      new Promise(() => {});
      return true;
    } catch {
      return false;
    }
  }

  private checkAsyncAwait(): boolean {
    try {
      // Simple async/await syntax check
      new Function('return async () => {}');
      return true;
    } catch {
      return false;
    }
  }

  private checkModules(): boolean {
    return 'noModule' in HTMLScriptElement.prototype;
  }

  private checkWebPSupport(): boolean {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  private async checkAVIFSupport(): Promise<boolean> {
    return new Promise(resolve => {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      canvas.toBlob(blob => {
        resolve(blob !== null);
      }, 'image/avif');
    });
  }

  private isBrowserSupported(browserInfo: Omit<BrowserInfo, 'supportedFeatures' | 'unsupportedFeatures' | 'isSupported'>, unsupportedFeatures: string[]): boolean {
    const supportedBrowser = this.SUPPORTED_BROWSERS[browserInfo.name as keyof typeof this.SUPPORTED_BROWSERS];
    
    if (!supportedBrowser) return false;
    
    const versionSupported = parseInt(browserInfo.version) >= supportedBrowser.minVersion;
    const featuresSupported = unsupportedFeatures.length <= 2; // Allow up to 2 unsupported features
    
    return versionSupported && featuresSupported;
  }

  validatePerformanceTargets(currentMetrics: Record<string, number>): PerformanceTarget[] {
    const targets: PerformanceTarget[] = [];

    Object.entries(this.PERFORMANCE_TARGETS).forEach(([key, config]) => {
      const currentValue = currentMetrics[key] || 0;
      targets.push({
        name: config.name,
        target: config.target,
        current: currentValue,
        passed: currentValue <= config.target,
        unit: config.unit
      });
    });

    return targets;
  }

  generateRecommendations(browserInfo: BrowserInfo, performanceTargets: PerformanceTarget[]): string[] {
    const recommendations: string[] = [];

    // Browser recommendations
    if (!browserInfo.isSupported) {
      if (browserInfo.unsupportedFeatures.length > 0) {
        recommendations.push(`Update your browser to support missing features: ${browserInfo.unsupportedFeatures.join(', ')}`);
      }
      
      const supportedBrowser = this.SUPPORTED_BROWSERS[browserInfo.name as keyof typeof this.SUPPORTED_BROWSERS];
      if (supportedBrowser && parseInt(browserInfo.version) < supportedBrowser.minVersion) {
        recommendations.push(`Update ${browserInfo.name} to version ${supportedBrowser.minVersion} or higher for optimal performance`);
      }
    }

    // Performance recommendations
    const failedTargets = performanceTargets.filter(target => !target.passed);
    if (failedTargets.length > 0) {
      recommendations.push('Performance targets not met:');
      failedTargets.forEach(target => {
        recommendations.push(`  - ${target.name}: ${target.current}${target.unit} (target: ${target.target}${target.unit})`);
      });
    }

    // General recommendations
    if (browserInfo.name === 'safari' && parseInt(browserInfo.version) < 15) {
      recommendations.push('Consider using Chrome or Firefox for better WebGL performance');
    }

    if (performanceTargets.some(t => t.name === 'Memory Usage' && !t.passed)) {
      recommendations.push('Close other browser tabs to reduce memory usage');
    }

    return recommendations;
  }

  async analyzeBrowserSupport(currentMetrics: Record<string, number> = {}): Promise<BrowserSupportResult> {
    const browser = await this.detectBrowser();
    const performanceTargets = this.validatePerformanceTargets(currentMetrics);
    const recommendations = this.generateRecommendations(browser, performanceTargets);

    return {
      browser,
      performanceTargets,
      overallSupport: browser.isSupported && performanceTargets.every(t => t.passed),
      recommendations
    };
  }
}

// Singleton instance
export const browserSupportDetector = new BrowserSupportDetector();

// Convenience functions
export const detectBrowserSupport = () => browserSupportDetector.detectBrowser();
export const validatePerformanceTargets = (metrics: Record<string, number>) => 
  browserSupportDetector.validatePerformanceTargets(metrics);
export const analyzeBrowserSupport = (metrics?: Record<string, number>) => 
  browserSupportDetector.analyzeBrowserSupport(metrics);
